import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, PageHeader } from '@/components/ui'
import { supabase } from '@/lib/supabase'
import { PetAvatar } from '@/components/PetAvatar'
import { usePetName } from '@/hooks/usePetName'

type Felt = 'happy' | 'tough' | 'turning-point' | 'neutral'
type Category = 'work' | 'school' | 'hobby' | 'relationship' | 'life-event'

interface PhaseEvent {
  id: number
  user_id: string
  phase: string | null
  year_from: number | null
  year_to: number | null
  category: Category | null
  title: string
  body: string | null
  felt: Felt | null
  created_at: string
  updated_at: string
}

interface Candidate {
  phase?: string | null
  year_from?: number | null
  year_to?: number | null
  category?: Category | null
  title: string
  body?: string | null
  felt?: Felt | null
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface SelfDocSection {
  kind?: string
  title?: string
  items?: Array<{ text?: string }>
}

const CATEGORY_LABEL: Record<Category, string> = {
  work: '仕事',
  school: '学校',
  hobby: '部活・趣味',
  relationship: '人間関係',
  'life-event': '人生イベント',
}

const FELT_LABEL: Record<Felt, string> = {
  happy: '幸せ',
  tough: '大変',
  'turning-point': '転換点',
  neutral: '中立',
}

const EMPTY_CANDIDATE: Candidate = {
  phase: '',
  year_from: null,
  year_to: null,
  category: 'life-event',
  title: '',
  body: '',
  felt: 'neutral',
}

async function callRootsDialogue(message: string, history: ChatMessage[], events: PhaseEvent[]) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token || ''
  const res = await fetch(import.meta.env.VITE_SUPABASE_URL + '/functions/v1/roots-dialogue', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      message,
      history,
      existing_events: events.slice(0, 30).map((e) => ({
        phase: e.phase,
        year_from: e.year_from,
        year_to: e.year_to,
        category: e.category,
        title: e.title,
        body: e.body,
        felt: e.felt,
      })),
    }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`roots-dialogue ${res.status}: ${text.substring(0, 200)}`)
  }
  return res.json() as Promise<{ content: string; candidates?: Candidate[] }>
}

export function Roots() {
  const { name: petName } = usePetName()
  const [events, setEvents] = useState<PhaseEvent[]>([])
  const [selfDocLines, setSelfDocLines] = useState<string[]>([])
  const [history, setHistory] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [draft, setDraft] = useState<Candidate>(EMPTY_CANDIDATE)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const [eventsRes, docRes] = await Promise.all([
      supabase
        .from('phase_events')
        .select('id, user_id, phase, year_from, year_to, category, title, body, felt, created_at, updated_at')
        .order('year_from', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true }),
      supabase
        .from('self_doc')
        .select('sections')
        .eq('is_current', true)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ])
    setEvents((eventsRes.data ?? []) as PhaseEvent[])
    const sections = ((docRes.data?.sections ?? []) as SelfDocSection[])
    const phaseSection = sections.find((s) => s.kind === 'phase_timeline')
    setSelfDocLines((phaseSection?.items ?? []).map((item) => item.text ?? '').filter(Boolean))
    setLoading(false)
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [history])

  const grouped = useMemo(() => {
    const map = new Map<string, PhaseEvent[]>()
    for (const event of events) {
      const key = event.phase?.trim() || '時期未設定'
      map.set(key, [...(map.get(key) ?? []), event])
    }
    return Array.from(map.entries())
  }, [events])

  async function send() {
    const text = input.trim()
    if (!text || sending) return
    setError(null)
    setSending(true)
    const nextHistory = [...history, { role: 'user' as const, content: text }]
    setHistory(nextHistory)
    setInput('')
    try {
      const result = await callRootsDialogue(text, history, events)
      setHistory([...nextHistory, { role: 'assistant', content: result.content || 'もう少し聞かせてください。' }])
      setCandidates(result.candidates ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI 対話に失敗しました')
      setHistory(nextHistory)
    } finally {
      setSending(false)
    }
  }

  async function saveCandidate(candidate: Candidate) {
    if (!candidate.title.trim()) return
    setSaving(true)
    setError(null)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setError('ログイン状態を確認できませんでした')
      setSaving(false)
      return
    }
    const { error: insertError } = await supabase.from('phase_events').insert({
      user_id: user.id,
      phase: candidate.phase?.trim() || null,
      year_from: candidate.year_from ?? null,
      year_to: candidate.year_to ?? null,
      category: candidate.category ?? 'life-event',
      title: candidate.title.trim(),
      body: candidate.body?.trim() || null,
      felt: candidate.felt ?? 'neutral',
    })
    setSaving(false)
    if (insertError) {
      setError(insertError.message)
      return
    }
    setDraft(EMPTY_CANDIDATE)
    setCandidates((prev) => prev.filter((c) => c !== candidate))
    await load()
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      void send()
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="ルーツ"
        description="子供時代、部活、仕事の遍歴、転換点を相棒と対話しながらフェーズ別に整理します。"
      />

      {error && (
        <Card style={{ marginBottom: 12, borderLeft: '3px solid var(--red)' }}>
          <div style={{ fontSize: 12, color: 'var(--red)' }}>{error}</div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        <section>
          <Card style={{ marginBottom: 16, background: 'var(--accent-bg)', border: '1px solid var(--accent-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <PetAvatar size={32} />
              <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, flex: 1 }}>{petName}</div>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>Roots dialogue</span>
            </div>

            {history.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.7, padding: '4px 2px 12px' }}>
                まずは「小学生の頃に楽しかったこと」「部活で覚えている場面」「最初の仕事で大変だったこと」など、断片で大丈夫です。
              </div>
            )}

            {history.length > 0 && (
              <div
                ref={scrollRef}
                style={{
                  maxHeight: 360,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  padding: '4px 2px 12px',
                  marginBottom: 10,
                  borderBottom: '1px solid var(--border)',
                }}
              >
                {history.map((message, i) => (
                  <div
                    key={i}
                    style={{
                      alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '88%',
                      padding: '9px 13px',
                      borderRadius: 10,
                      background: message.role === 'user' ? 'var(--surface2)' : 'rgba(255,255,255,0.6)',
                      border: message.role === 'assistant' ? '1px solid var(--border)' : 'none',
                      fontSize: 13,
                      color: 'var(--text)',
                      lineHeight: 1.7,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {message.content}
                  </div>
                ))}
                {sending && <div style={{ fontSize: 11, color: 'var(--text3)' }}>考え中...</div>}
              </div>
            )}

            <textarea
              className="input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="思い出したことを書く（Cmd/Ctrl+Enterで送信）"
              disabled={sending}
              style={{ width: '100%', boxSizing: 'border-box', minHeight: 76, fontSize: 13, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={() => void send()} disabled={!input.trim() || sending}>
                送信
              </button>
            </div>
          </Card>

          {candidates.length > 0 && (
            <section style={{ marginBottom: 18 }}>
              <div className="section-title">保存候補</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {candidates.map((candidate, i) => (
                  <CandidateCard key={i} candidate={candidate} onChange={(next) => setCandidates((prev) => prev.map((c, idx) => idx === i ? next : c))} onSave={() => saveCandidate(candidate)} saving={saving} />
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="section-title">手動で追加</div>
            <CandidateCard candidate={draft} onChange={setDraft} onSave={() => saveCandidate(draft)} saving={saving} />
          </section>
        </section>

        <aside>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>フェーズ年表</div>
            {loading ? (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>読み込み中...</div>
            ) : grouped.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.7 }}>
                まだ保存された出来事がありません。対話から候補を作って保存してください。
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {grouped.map(([phase, rows]) => (
                  <div key={phase}>
                    <div style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600, marginBottom: 6 }}>{phase}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {rows.map((event) => <PhaseEventEditor key={event.id} event={event} onSaved={load} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {selfDocLines.length > 0 && (
            <Card>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>self_doc からの初期ヒント</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selfDocLines.map((line, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7, paddingLeft: 10, borderLeft: '2px solid var(--border)' }}>
                    {line}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </aside>
      </div>
    </div>
  )
}

function CandidateCard({
  candidate,
  onChange,
  onSave,
  saving,
}: {
  candidate: Candidate
  onChange: (next: Candidate) => void
  onSave: () => void
  saving: boolean
}) {
  return (
    <Card style={{ padding: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: 8, marginBottom: 8 }}>
        <input className="input" value={candidate.phase ?? ''} onChange={(e) => onChange({ ...candidate, phase: e.target.value })} placeholder="フェーズ（例: 小学校、社会人1年目）" />
        <input className="input" type="number" value={candidate.year_from ?? ''} onChange={(e) => onChange({ ...candidate, year_from: e.target.value ? Number(e.target.value) : null })} placeholder="開始年" />
        <input className="input" type="number" value={candidate.year_to ?? ''} onChange={(e) => onChange({ ...candidate, year_to: e.target.value ? Number(e.target.value) : null })} placeholder="終了年" />
      </div>
      <input className="input" value={candidate.title} onChange={(e) => onChange({ ...candidate, title: e.target.value })} placeholder="出来事のタイトル" style={{ width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
      <textarea className="input" value={candidate.body ?? ''} onChange={(e) => onChange({ ...candidate, body: e.target.value })} placeholder="何があり、どう感じたか" rows={3} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', marginBottom: 8 }} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="input" value={candidate.category ?? 'life-event'} onChange={(e) => onChange({ ...candidate, category: e.target.value as Category })} style={{ width: 130 }}>
          {Object.entries(CATEGORY_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
        <select className="input" value={candidate.felt ?? 'neutral'} onChange={(e) => onChange({ ...candidate, felt: e.target.value as Felt })} style={{ width: 120 }}>
          {Object.entries(FELT_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving || !candidate.title.trim()}>
          {saving ? '保存中...' : '保存する'}
        </button>
      </div>
    </Card>
  )
}

function PhaseEventEditor({ event, onSaved }: { event: PhaseEvent; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Candidate>(event)
  const [saving, setSaving] = useState(false)

  async function save() {
    if (!draft.title.trim()) return
    setSaving(true)
    const { error } = await supabase
      .from('phase_events')
      .update({
        phase: draft.phase?.trim() || null,
        year_from: draft.year_from ?? null,
        year_to: draft.year_to ?? null,
        category: draft.category ?? 'life-event',
        title: draft.title.trim(),
        body: draft.body?.trim() || null,
        felt: draft.felt ?? 'neutral',
        updated_at: new Date().toISOString(),
      })
      .eq('id', event.id)
    setSaving(false)
    if (error) {
      alert('保存に失敗しました: ' + error.message)
      return
    }
    setEditing(false)
    onSaved()
  }

  if (editing) {
    return (
      <CandidateCard
        candidate={draft}
        onChange={setDraft}
        onSave={() => void save()}
        saving={saving}
      />
    )
  }

  const years = event.year_from || event.year_to
    ? `${event.year_from ?? '?'}-${event.year_to ?? ''}`
    : ''

  return (
    <div style={{ padding: 10, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <div style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600, flex: 1 }}>{event.title}</div>
        {years && <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{years}</span>}
      </div>
      {event.body && <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.6, marginTop: 4 }}>{event.body}</div>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
        {event.category && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{CATEGORY_LABEL[event.category]}</span>}
        {event.felt && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{FELT_LABEL[event.felt]}</span>}
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 10 }} onClick={() => setEditing(true)}>編集</button>
      </div>
    </div>
  )
}
